/**
 * Translation between AG Grid's client filter/sort models and the query params
 * declared server-side by GridFilterSetBuilder
 * (backend/homebase/homebase/filters/grid_filter_set_builder.py), plus the URL
 * round-tripping that lets a filtered, sorted grid reload and share as a link.
 *
 * A page describes its server-filterable columns once as a GridFieldConfig and
 * gets matching translators; anything page-specific (custom operators, exact-id
 * columns, ...) is layered on top by wrapping translateFieldCondition.
 */

export interface SingleCondition {
  filterType: string;
  type?: string;
  filter?: string | number;
  filterTo?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface FilterModel {
  [key: string]: SingleCondition & {
    // Set instead of `type` etc. when both of a column filter's condition
    // slots are filled in.
    operator?: 'AND' | 'OR';
    conditions?: Array<Omit<SingleCondition, 'filterType'> & { filterType?: string }>;
  };
}

export type SingleFilter = FilterModel[string];

export interface SortModelItem {
  colId: string;
  sort: 'asc' | 'desc';
}

export interface ConditionGroup {
  operator: 'AND' | 'OR';
  conditions: Array<Record<string, string>>;
}

/** Which server params each column supports -- mirrors the builder calls server-side. */
export interface GridFieldConfig {
  /** `.text(...)`: `<field>`, `_equals`, `_not_contains`, ... */
  text: ReadonlySet<string>;
  /** `.date(...)`: `_from`/`_to`/`_after`/`_before`. */
  date: ReadonlySet<string>;
  /** `.numeric(...)`: `_min`/`_max`. */
  number: ReadonlySet<string>;
  /** `.isnull(...)`: `<field>_isnull`, for AG Grid's blank/notBlank. */
  blank: ReadonlySet<string>;
  /** `.ordering(...)`. */
  sortable: ReadonlySet<string>;
}

export type ConditionTranslator = (
  field: string,
  filter: SingleCondition
) => Record<string, string>;

export type ConditionMatcher<Row> = (row: Row, field: string, filter: SingleFilter) => boolean;

const BLANK_FILTER_TYPES = new Set(['blank', 'notBlank']);

// AG Grid text-filter operator -> the server's `<field><suffix>` param
// (GridFilterSetBuilder.TEXT_OPERATOR_LOOKUPS). Each operator needs its own
// param: sending e.g. notContains as the bare icontains `<field>` would fetch
// only the rows the grid's own client-side re-filter then drops, leaving an
// empty page.
export const TEXT_OPERATOR_PARAM_SUFFIXES: Record<string, string> = {
  contains: '',
  notContains: '_not_contains',
  equals: '_equals',
  notEqual: '_not_equal',
  startsWith: '_starts_with',
  endsWith: '_ends_with'
};

/**
 * A single filter condition's query params for a column described by
 * `config`. Returns `{}` for a condition with no server equivalent, which
 * then narrows nothing server-side (the grid's client-side re-filter still
 * applies it to the fetched page).
 */
export const translateFieldCondition = (
  config: GridFieldConfig,
  field: string,
  filter: SingleCondition
): Record<string, string> => {
  if (config.blank.has(field) && filter.type && BLANK_FILTER_TYPES.has(filter.type)) {
    return { [`${field}_isnull`]: filter.type === 'blank' ? 'true' : 'false' };
  }

  // Boolean columns: AG Grid's boolean filter stores its operator as the
  // filter type ('true'/'false') with no operand. Keyed off the type rather
  // than the field so any boolean column translates the same way.
  if (filter.type === 'true' || filter.type === 'false') {
    return { [field]: filter.type };
  }

  if (config.text.has(field) && filter.filterType === 'text' && filter.filter) {
    const suffix = TEXT_OPERATOR_PARAM_SUFFIXES[filter.type ?? 'contains'];
    return suffix === undefined ? {} : { [`${field}${suffix}`]: String(filter.filter) };
  }

  if (config.date.has(field) && filter.filterType === 'date') {
    if (filter.type === 'inRange' && filter.dateFrom && filter.dateTo) {
      // AG Grid's inRange is inclusive both ends when inRangeInclusive is set
      // (see the date columns' filterParams), matching _from/_to's gte/lte.
      return { [`${field}_from`]: filter.dateFrom, [`${field}_to`]: filter.dateTo };
    }
    if (filter.type === 'greaterThan' && filter.dateFrom) {
      // AG Grid's greaterThan compares strictly, unlike _from's inclusive gte
      // -- route to the exclusive _after param instead so a row on the
      // boundary isn't returned by the server just to be dropped again by the
      // grid's own client-side re-filter of the fetched page.
      return { [`${field}_after`]: filter.dateFrom };
    }
    if (filter.type === 'lessThan' && filter.dateFrom) {
      return { [`${field}_before`]: filter.dateFrom };
    }
    return {};
  }

  if (config.number.has(field) && filter.filterType === 'number') {
    const value = filter.filter !== undefined ? String(filter.filter) : undefined;
    const valueTo = filter.filterTo !== undefined ? String(filter.filterTo) : undefined;

    if (filter.type === 'inRange' && value && valueTo) {
      return { [`${field}_min`]: value, [`${field}_max`]: valueTo };
    }
    if (filter.type === 'equals' && value) {
      return { [`${field}_min`]: value, [`${field}_max`]: value };
    }
    if ((filter.type === 'greaterThanOrEqual' || filter.type === 'greaterThan') && value) {
      return { [`${field}_min`]: value };
    }
    if ((filter.type === 'lessThanOrEqual' || filter.type === 'lessThan') && value) {
      return { [`${field}_max`]: value };
    }
  }

  return {};
};

/**
 * A two-condition column filter (AG Grid's `operator` + `conditions`) can't be
 * expressed as flat query params -- OR has no flat equivalent, and an AND of two
 * same-operator conditions would collide on one param. Each condition is
 * translated on its own (so it gets exactly the same params as a single-condition
 * filter would) and sent as a group in the JSON `condition_groups` param, which
 * the server evaluates condition by condition (ConditionGroupFilterSet).
 *
 * A condition with no server equivalent can't narrow the query: under AND it's
 * dropped (the rest still narrow), but under OR it would have to match
 * everything, so the whole group is dropped. Either way the grid's own
 * client-side re-filter still applies it to the fetched page.
 */
const translateConditionGroup = (
  translateCondition: ConditionTranslator,
  field: string,
  filter: SingleFilter
): ConditionGroup | null => {
  const operator = filter.operator === 'OR' ? 'OR' : 'AND';
  const translated = (filter.conditions ?? []).map((condition) =>
    translateCondition(field, {
      ...condition,
      filterType: condition.filterType ?? filter.filterType
    })
  );
  const conditions = translated.filter((params) => Object.keys(params).length > 0);

  if (conditions.length === 0) return null;
  if (operator === 'OR' && conditions.length < translated.length) return null;

  return { operator, conditions };
};

/**
 * Translates AG Grid's filter model into list-endpoint query params, one column
 * at a time via `translateCondition`; two-condition column filters go in
 * `condition_groups` instead (see translateConditionGroup).
 */
export const translateFilterModelWith = (
  translateCondition: ConditionTranslator,
  filterModel: FilterModel
): Record<string, string> => {
  const columnFilters: Record<string, string> = {};
  const conditionGroups: ConditionGroup[] = [];

  Object.keys(filterModel).forEach((field) => {
    const filter = filterModel[field];

    if (filter.conditions) {
      const group = translateConditionGroup(translateCondition, field, filter);
      if (group) conditionGroups.push(group);
      return;
    }

    Object.assign(columnFilters, translateCondition(field, filter));
  });

  if (conditionGroups.length > 0) {
    columnFilters['condition_groups'] = JSON.stringify(conditionGroups);
  }

  return columnFilters;
};

/**
 * Translates AG Grid's sort model into the `ordering` query param. Columns the
 * server can't sort by should be marked unsortable in the column defs in the
 * first place, but are defensively dropped here too.
 */
export const translateSortModelWith = (
  sortable: ReadonlySet<string>,
  sortModel: SortModelItem[]
): string | undefined => {
  const tokens = sortModel
    .filter((s) => sortable.has(s.colId))
    .map((s) => (s.sort === 'desc' ? `-${s.colId}` : s.colId));

  return tokens.length > 0 ? tokens.join(',') : undefined;
};

export const isBlank = (value: unknown): boolean => {
  if (value == null) return true;
  if (typeof value === 'string') return value.length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

export const matchesTextCondition = (
  value: string,
  type: string | undefined,
  filter: unknown
): boolean => {
  const needle = String(filter ?? '').toLowerCase();
  const haystack = value.toLowerCase();
  switch (type) {
    case 'equals':
      return haystack === needle;
    case 'notEqual':
      return haystack !== needle;
    case 'contains':
      return haystack.includes(needle);
    case 'notContains':
      return !haystack.includes(needle);
    case 'startsWith':
      return haystack.startsWith(needle);
    case 'endsWith':
      return haystack.endsWith(needle);
    default:
      return true;
  }
};

export const matchesNumberCondition = (
  value: number,
  type: string | undefined,
  filter?: number,
  filterTo?: number
): boolean => {
  switch (type) {
    case 'equals':
      return filter !== undefined && value === filter;
    case 'notEqual':
      return filter !== undefined && value !== filter;
    case 'inRange':
      return filter !== undefined && filterTo !== undefined && value >= filter && value <= filterTo;
    case 'greaterThan':
      return filter !== undefined && value > filter;
    case 'greaterThanOrEqual':
      return filter !== undefined && value >= filter;
    case 'lessThan':
      return filter !== undefined && value < filter;
    case 'lessThanOrEqual':
      return filter !== undefined && value <= filter;
    default:
      return true;
  }
};

export const matchesDateCondition = (
  value: string,
  type: string | undefined,
  dateFrom?: string,
  dateTo?: string
): boolean => {
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return false;

  const fromTime = dateFrom ? new Date(dateFrom).getTime() : undefined;
  const toTime = dateTo ? new Date(dateTo).getTime() : undefined;

  switch (type) {
    case 'equals':
      return fromTime !== undefined && time === fromTime;
    case 'notEqual':
      return fromTime !== undefined && time !== fromTime;
    case 'inRange':
      return fromTime !== undefined && toTime !== undefined && time >= fromTime && time <= toTime;
    case 'greaterThan':
    case 'after':
      return fromTime !== undefined && time > fromTime;
    case 'lessThan':
    case 'before':
      return fromTime !== undefined && time < fromTime;
    default:
      return true;
  }
};

/**
 * Evaluates a single filter condition against a row value client-side, with
 * the same semantics translateFieldCondition gives it server-side. Unrecognized
 * field/filter combinations return true (assume still visible) so a caller
 * never incorrectly drops a valid selection.
 */
export const matchesFieldCondition = (
  config: GridFieldConfig,
  rawValue: unknown,
  field: string,
  filter: SingleFilter
): boolean => {
  if (config.blank.has(field) && filter.type === 'blank') return isBlank(rawValue);
  if (config.blank.has(field) && filter.type === 'notBlank') return !isBlank(rawValue);

  // Boolean columns: AG Grid's boolean filter uses the operator as the type
  // ('true'/'false'), keyed off the type rather than the field.
  if (filter.type === 'true') return rawValue === true;
  if (filter.type === 'false') return rawValue === false;

  if (config.text.has(field) && filter.filterType === 'text') {
    return matchesTextCondition(String(rawValue ?? ''), filter.type, filter.filter);
  }

  if (config.date.has(field) && filter.filterType === 'date') {
    return matchesDateCondition(
      String(rawValue ?? ''),
      filter.type,
      filter.dateFrom,
      filter.dateTo
    );
  }

  if (config.number.has(field) && filter.filterType === 'number') {
    const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue);
    if (Number.isNaN(numericValue)) return false;
    return matchesNumberCondition(
      numericValue,
      filter.type,
      typeof filter.filter === 'number' ? filter.filter : undefined,
      filter.filterTo
    );
  }

  return true;
};

/**
 * Whether `row` would still satisfy every field in `filterModel`, evaluating
 * each condition with `matchCondition` and combining two-condition filters by
 * their operator.
 */
export const rowMatchesFilterModelWith = <Row>(
  matchCondition: ConditionMatcher<Row>,
  row: Row,
  filterModel: FilterModel
): boolean =>
  Object.entries(filterModel).every(([field, filter]) => {
    if (!filter.conditions) return matchCondition(row, field, filter);

    const matches = filter.conditions.map((condition) =>
      matchCondition(row, field, {
        ...condition,
        filterType: condition.filterType ?? filter.filterType
      })
    );
    return filter.operator === 'OR' ? matches.some(Boolean) : matches.every(Boolean);
  });

// The grid's own filter/sort state, mirrored onto the URL so a filtered, sorted
// view reloads and shares as a link. Kept separate from the translated request
// params: those are lossy -- several grid operators collapse onto the same
// `<field>_min`/`_from` param -- so the URL carries the grid model itself and
// lets the grid do the translating on restore.
const FILTER_MODEL_PARAM = 'filters';
const SORT_PARAM = 'sort';
const PAGE_PARAM = 'page';

/**
 * Reads a grid filter model back out of a URL query string. A missing or
 * unparseable param, or a malformed column, yields no filter for it rather
 * than throwing -- a hand-edited or
 * truncated link should still load the unfiltered grid.
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

// Valid JSON isn't necessarily a filter model -- `{"name": null}` or a
// non-array `conditions` would crash the translators that walk it.
const isColumnFilter = (value: unknown): value is SingleFilter =>
  isPlainObject(value) &&
  typeof value.filterType === 'string' &&
  (value.conditions === undefined ||
    (Array.isArray(value.conditions) && value.conditions.every(isPlainObject)));

export const filterModelFromSearch = (search: string): FilterModel => {
  const raw = new URLSearchParams(search).get(FILTER_MODEL_PARAM);
  if (!raw) return {};

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainObject(parsed)) return {};
    // Malformed columns are dropped individually, so the rest of a
    // hand-edited link still applies.
    return Object.fromEntries(
      Object.entries(parsed).filter(([, filter]) => isColumnFilter(filter))
    ) as FilterModel;
  } catch {
    return {};
  }
};

/**
 * Reads a sort model out of a URL query string, written in the same
 * `-field,field` shape as the backend's `ordering` param so the URL stays
 * readable. Columns the server can't sort by are dropped.
 */
export const sortModelFromSearchWith = (
  sortable: ReadonlySet<string>,
  search: string
): SortModelItem[] => {
  const raw = new URLSearchParams(search).get(SORT_PARAM);
  if (!raw) return [];

  return raw
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => ({
      colId: token.startsWith('-') ? token.slice(1) : token,
      sort: token.startsWith('-') ? ('desc' as const) : ('asc' as const)
    }))
    .filter((item) => sortable.has(item.colId));
};

/**
 * Reads the current page out of a URL query string. A missing, non-numeric,
 * or non-positive value falls back to page 1 rather than throwing, so a
 * hand-edited or truncated link still loads.
 */
export const pageFromSearch = (search: string): number => {
  const raw = new URLSearchParams(search).get(PAGE_PARAM);
  const page = Number(raw);

  return Number.isInteger(page) && page > 0 ? page : 1;
};

/**
 * Writes the current page into a URL query string, leaving every other param
 * untouched. Page 1 is the default landing state, so it's dropped rather than
 * written, keeping an unpaginated view's URL clean.
 */
export const pageToSearch = (search: string, page: number): string => {
  const params = new URLSearchParams(search);

  if (page > 1) {
    params.set(PAGE_PARAM, String(page));
  } else {
    params.delete(PAGE_PARAM);
  }

  return params.toString();
};

/**
 * Writes the grid's filter and sort model into a URL query string, replacing
 * whatever they held and leaving every other param untouched. An empty model
 * drops the param entirely so a cleared grid gives a clean URL.
 */
export const gridStateToSearch = (
  search: string,
  filterModel: FilterModel,
  sortModel: SortModelItem[]
): string => {
  const params = new URLSearchParams(search);

  if (Object.keys(filterModel).length > 0) {
    params.set(FILTER_MODEL_PARAM, JSON.stringify(filterModel));
  } else {
    params.delete(FILTER_MODEL_PARAM);
  }

  const ordering = sortModel.map((s) => (s.sort === 'desc' ? `-${s.colId}` : s.colId)).join(',');
  if (ordering) {
    params.set(SORT_PARAM, ordering);
  } else {
    params.delete(SORT_PARAM);
  }

  return params.toString();
};
